import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from 'store'
import { getInitials } from 'helpers/UserHelper';
import { IJwtPayload } from 'models/IJwtPayLoad';
import { ITokensData } from 'models/ITokensData';
import { IUser } from 'models/IUser'
import jwt_decode from "jwt-decode";

type AuthState = {
  user: IUser | null;
  accessToken: string | null;
  refreshToken: string | null;
}

const localStorageKey: string = "tpk";
const getStoredTokens: () => ITokensData | null = () => {
    const value: string | null = localStorage.getItem(localStorageKey);
    if (value === null || value === "undefined" || value.trim() === "") {
        return null;
    }

    try {
        const parsedValue: unknown = JSON.parse(value);
        if (parsedValue === null || typeof parsedValue !== "object") {
            return null;
        }

        return parsedValue as ITokensData;
    } catch {
        localStorage.removeItem(localStorageKey);
        return null;
    }
}

const getAuthUser: () => IUser | null = () => {
    const keys: ITokensData | null = getStoredTokens();
    if (keys === null || keys.accessToken === null) {
        return null;
    }

    const jwtDecode: IJwtPayload = jwt_decode<IJwtPayload>(keys.accessToken);
    if (jwtDecode.role === undefined) {
        throw new Error('Role is undefined');
    }
    return {
        email: jwtDecode.email,
        id: jwtDecode.sid,
        firstName: jwtDecode.given_name,
        lastName: jwtDecode.family_name,
        initials: getInitials(jwtDecode.given_name, jwtDecode.family_name),
        role: parseInt(jwtDecode.role),
        provider: jwtDecode.provider
    };
}

const getAccessToken: () => string | null = () => {
    const keys: ITokensData | null = getStoredTokens();
    if (keys === null || keys.accessToken === null) {
        return null;
    }

    return keys.accessToken;
}

const getRefreshToken: () => string | null = () => {
    const keys: ITokensData | null = getStoredTokens();
    if (keys === null || keys.refreshToken === null) {
        return null;
    }

    return keys.refreshToken;
}

const initialState: AuthState = {
    user: getAuthUser(),
    accessToken: getAccessToken(),
    refreshToken: getRefreshToken()
}

const slice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    setCredentials: (state, { payload }: PayloadAction<ITokensData>
    ) => {
        localStorage.setItem(localStorageKey, JSON.stringify(payload));
        state.user = getAuthUser();
        state.accessToken = getAccessToken();
        state.refreshToken = getRefreshToken();
    },
    tokenReceived: (state, { payload }: PayloadAction<ITokensData>) => {
        localStorage.setItem(localStorageKey, JSON.stringify(payload));
        state.user = getAuthUser();
        state.accessToken = getAccessToken();
        state.refreshToken = getRefreshToken();
    },
    profileChanged: (state, { payload }: PayloadAction<{firstName: string, lastName: string, email: string}>) => {
        (state.user as IUser).firstName = payload.firstName;
        (state.user as IUser).lastName = payload.lastName;
        (state.user as IUser).email = payload.email;
        (state.user as IUser).initials = getInitials(payload.firstName, payload.lastName);
    },
    localLogout: (state) => {
        localStorage.clear();
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
    }
  },
})

export const { setCredentials, tokenReceived, profileChanged, localLogout } = slice.actions

export default slice.reducer

export const selectCurrentUser = (state: RootState) => { 
    return state.auth.user
}